/**
 * Signup entry rules.
 *
 * CheerVue accounts must be created from a gym's context so the account is
 * linked to that gym at creation time. The generic /register URL, reached with
 * no gym and no invitation, would otherwise create a gym-less account.
 *
 * Two contexts count as gym-attached:
 *   - organizationId — supplied by a gym catalog's "Create Account" button.
 *   - a family guardian invite; the invite acceptance step links the new account
 *     to the gym instead.
 *
 * Neither is taken on trust. organizationId is resolved against the database by
 * the caller, and the invited email must arrive with a signed proof minted by
 * the invitation's own validate route (familyInviteSignupToken.ts) — a
 * ?invitationEmail= typed into the address bar proves nothing and used to be
 * enough on its own.
 */

/**
 * True when /register was reached with enough context to attach a gym.
 *
 * `provenInvitationEmail` is named for what it must be: the email out of a
 * VERIFIED proof, not the one off the URL. Passing the raw query param here
 * re-opens the door this parameter exists to close.
 */
export function hasGymSignupContext(params: {
  organizationId?: string | null;
  provenInvitationEmail?: string | null;
}): boolean {
  return Boolean(params.organizationId?.trim()) || Boolean(params.provenInvitationEmail?.trim());
}

/**
 * Gym catalog page for an already-validated internal redirect path, or null.
 *
 * Used to point a signed-out visitor at the one page that can start signup for
 * the gym they were trying to reach. Only paths whose first segment is a gym
 * catalog route yield a slug.
 */
export function gymCatalogHrefFromRedirect(redirectTo: string | null | undefined): string | null {
  if (!redirectTo || !redirectTo.startsWith('/')) return null;
  const segments = redirectTo.split('?')[0].split('#')[0].split('/').filter(Boolean);
  if (segments.length < 2) return null;
  const [root, slug] = segments;
  if (root !== 'programs' && root !== 'register-tryout') return null;
  // The catalog lowercases the slug before looking a gym up (normalizeSlugForLookup),
  // so a mixed-case slug in the redirect still reaches the right gym — but the href
  // is lowercased to match the canonical catalog address.
  if (!/^[A-Za-z0-9-]+$/.test(slug)) return null;
  return `/programs/${slug.toLowerCase()}`;
}

/**
 * Absolute /register URL carrying the gym, for links sent outside the app.
 *
 * A bare /register shows the "start from your gym's page" explanation, so any
 * email or share link that means "create your account" must carry the gym or it
 * dead-ends every recipient. organizationName is cosmetic — the signup page and
 * the signup API both resolve the id server-side.
 */
export function signupUrlForOrganization(
  baseUrl: string,
  organizationId: string,
  organizationName?: string | null
): string {
  const params = new URLSearchParams({ organizationId });
  if (organizationName) params.set('organizationName', organizationName);
  return `${baseUrl.replace(/\/+$/, '')}/register?${params.toString()}`;
}
